import React from "react";
import { HERO_IMAGE, COMPANY } from "@/lib/site-data";
import { HOME } from "@/constants/testIds";
import { ArrowRight, Phone, Truck, Lightning, CurrencyDollar } from "@phosphor-icons/react";

const Pill = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2">
    <Icon size={18} weight="bold" className="text-sky-500" />
    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-900">{label}</span>
  </div>
);

export default function Hero() {
  return (
    <section id="top" className="relative bg-white overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 bg-grid-slate opacity-60 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left */}
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 border-2 border-sky-500 bg-sky-50 px-3 py-1.5 mb-7">
              <span className="h-2 w-2 bg-sky-500 animate-pulse" />
              <span className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-700">
                Same-Day Delivery · Toronto + GTA
              </span>
            </div>

            <h1
              data-testid={HOME.heroHeading}
              className="font-display font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
            >
              Bin rental,
              <br />
              <span className="text-sky-500">done right.</span>
              <br />
              <span className="text-slate-700">Today.</span>
            </h1>

            <p className="mt-7 text-lg lg:text-xl text-slate-600 max-w-xl leading-relaxed">
              From small reno cleanouts to whole-house tear-offs &mdash; Troya Mini Bins delivers 6, 10, 14, 16 &amp; 20-yard bins
              across {COMPANY.area}. Transparent pricing. Honest weight. Zero runaround.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a
                data-testid={HOME.ctaHeroQuote}
                href="/book"
                className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold uppercase tracking-[0.14em] text-sm border-2 border-sky-500 hover:border-sky-600 transition-colors"
              >
                Book Now
                <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                data-testid={HOME.ctaHeroCall}
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-slate-900 text-slate-900 font-bold uppercase tracking-[0.14em] text-sm hover:bg-slate-900 hover:text-white transition-colors"
              >
                <Phone size={20} weight="bold" />
                Call {COMPANY.phone}
              </a>
            </div>

            {/* Value pills */}
            <div className="mt-10 flex flex-wrap gap-3">
              <Pill icon={Lightning} label="Same-Day Delivery" />
              <Pill icon={CurrencyDollar} label="Transparent pricing" />
              <Pill icon={Truck} label="7-day rental incl." />
            </div>

            {/* HomeStars micro trust */}
            <a
              href="https://www.homestars.com/profile/2970106-troya-minibins"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-homestars-trust"
              className="mt-6 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-700 transition-colors group"
            >
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="text-amber-500 text-base">★</span>
                ))}
              </span>
              <span className="font-bold text-slate-900">5.0</span>
              <span>·</span>
              <span>
                <span className="font-bold text-emerald-700">7 verified reviews</span> on HomeStars
              </span>
              <span className="text-slate-400 group-hover:translate-x-0.5 transition-transform">↗</span>
            </a>
          </div>

          {/* Right — image with offset block */}
          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -top-4 -left-4 right-8 bottom-8 bg-sky-500 -z-0" />
            <div className="absolute top-8 left-8 -right-4 -bottom-4 border-2 border-slate-900 -z-0" />
            <div className="relative z-10 bg-slate-100 border-2 border-slate-900 overflow-hidden">
              <img
                src={HERO_IMAGE}
                alt="Troya Mini Bins truck lifting a branded blue roll-off bin on a Toronto job site"
                className="w-full h-[420px] lg:h-[520px] object-cover"
                style={{ objectPosition: "center center" }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur-sm p-4">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-sky-400">Now serving</div>
                <div className="text-white font-display font-bold text-lg mt-0.5">Toronto + Greater Toronto Area</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audience marquee */}
      <div data-testid={HOME.trustStrip} className="relative border-y-2 border-slate-900 bg-slate-900 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-4">
          {[
            "Same-Day Delivery",
            "Toronto + GTA",
            "Transparent Pricing",
            "Honest Weighing",
            "5.0 ★ on HomeStars",
            "7 Days Rental Included",
          ]
            .concat([
              "Same-Day Delivery",
              "Toronto + GTA",
              "Transparent Pricing",
              "Honest Weighing",
              "5.0 ★ on HomeStars",
              "7 Days Rental Included",
            ])
            .map((a, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-4 px-8 text-white font-display font-extrabold text-base uppercase tracking-[0.22em]"
              >
                {a}
                <span className="h-1.5 w-1.5 bg-sky-400" />
              </span>
            ))}
        </div>
      </div>
    </section>
  );
}
